import {
  Box3,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  EdgesGeometry,
  LineBasicMaterial,
  LineSegments,
  Material,
  Matrix3,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Path,
  Shape,
  ShapeGeometry,
  Texture,
  Vector2,
  Vector3,
} from "three";
import { OBB } from "three/examples/jsm/math/OBB.js";

/**
 * three.js OBB.applyMatrix4 has a bug: it adds matrix translation to center
 * without rotating the center vector, and composes rotation in the wrong order.
 * This helper does the correct transform: world = matrix * obb.
 */
export function transformObb(obb: OBB, matrix: Matrix4): OBB {
  const result = obb.clone();
  result.center.applyMatrix4(matrix);
  const m3 = new Matrix3().setFromMatrix4(matrix);
  result.rotation.premultiply(m3);
  return result;
}

export const DEFAULT_ROOM = {
  points: [
    { x: -3.5, y: 0, z: -2.5 },
    { x: 3.5, y: 0, z: -2.5 },
    { x: 3.5, y: 0, z: 2.5 },
    { x: -3.5, y: 0, z: 2.5 },
  ],
  lines: [
    { start: 0, end: 1, hidden: false, type: "Wall" },
    { start: 1, end: 2, hidden: false, type: "Wall" },
    { start: 2, end: 3, hidden: false, type: "Wall" },
    { start: 3, end: 0, hidden: false, type: "Wall" },
  ],
};

export const DEFAULT_ROOM_LARGE = {
  points: [
    { x: -4.5, y: 0, z: -3.5 },
    { x: 4.5, y: 0, z: -3.5 },
    { x: 4.5, y: 0, z: 3.5 },
    { x: -4.5, y: 0, z: 3.5 },
  ],
  lines: [
    { start: 0, end: 1, hidden: false, type: "Wall" },
    { start: 1, end: 2, hidden: false, type: "Wall" },
    { start: 2, end: 3, hidden: false, type: "Wall" },
    { start: 3, end: 0, hidden: false, type: "Wall" },
  ],
};

export function getBoundingBox(
  model: Object3D,
  size?: Vector3,
  center?: Vector3
): Box3 {
  const box = new Box3();

  model.updateWorldMatrix(true, true);
  box.setFromObject(model);
  if (size) {
    box.getSize(size);
  }
  if (center) {
    box.getCenter(center);
  }
  return box;
}

export function calculateBoundingBoxAll(model: Object3D): Mesh[] {
  const bounds: Mesh[] = [];
  model.traverse((o) => {
    if (o instanceof Mesh) {
      bounds.push(calculateBoundingBox(o));
    }
  });
  return bounds;
}

export function calculateBoundingBox(model: Mesh): Mesh {
  const box3 = new Box3();
  box3.setFromObject(model, true);

  const geometry = new BufferGeometry();
  const vertices = new Float32Array([
    // Arka yüz
    box3.min.x,
    box3.min.y,
    box3.min.z, // 0
    box3.max.x,
    box3.min.y,
    box3.min.z, // 1
    box3.max.x,
    box3.max.y,
    box3.min.z, // 2
    box3.min.x,
    box3.max.y,
    box3.min.z, // 3
    // Ön yüz
    box3.min.x,
    box3.min.y,
    box3.max.z, // 4
    box3.max.x,
    box3.min.y,
    box3.max.z, // 5
    box3.max.x,
    box3.max.y,
    box3.max.z, // 6
    box3.min.x,
    box3.max.y,
    box3.max.z, // 7
  ]);
  const indices = new Uint16Array([
    // Arka
    0, 2, 1, 0, 3, 2,
    // Ön
    4, 5, 6, 4, 6, 7,
    // Üst
    3, 7, 6, 3, 6, 2,
    // Alt
    0, 1, 5, 0, 5, 4,
    // Sağ
    1, 2, 6, 1, 6, 5,
    // Sol
    0, 4, 7, 0, 7, 3,
  ]);

  geometry.setIndex(new BufferAttribute(indices, 1));
  geometry.setAttribute("position", new BufferAttribute(vertices, 3));
  geometry.computeVertexNormals();

  const material = new MeshBasicMaterial({ color: 0xff000055 });
  material.transparent = true;
  material.opacity = 0.3;
  const mesh = new Mesh(geometry, material);
  return mesh;
}

export function createBox2d(length: number, thickness: number) {
  const shape = new Shape([
    new Vector2(0.0, 0.0),
    new Vector2(0.0, thickness),
    new Vector2(length, thickness),
    new Vector2(length, 0.0),
  ]);

  return new ShapeGeometry(shape);
}

export function createBoxOptions(
  x: number,
  y: number,
  z: number,
  options?: {
    front?: boolean;
    back?: boolean;
    top?: boolean;
    bottom?: boolean;
    right?: boolean;
    left?: boolean;
  }
) {
  const defaultOptions = {
    front: true,
    back: true,
    top: true,
    bottom: true,
    right: true,
    left: true,
  };

  options = { ...defaultOptions, ...options };

  const geometry = new BufferGeometry();
  const minx = -0.5 * x;
  const maxx = 0.5 * x;
  const miny = -0.5 * y;
  const maxy = 0.5 * y;
  const minz = -0.5 * z;
  const maxz = 0.5 * z;

  const vertices = new Float32Array([
    // Arka yüz
    minx,
    miny,
    minz, // 0
    maxx,
    miny,
    minz, // 1
    maxx,
    maxy,
    minz, // 2
    minx,
    maxy,
    minz, // 3

    minx,
    miny,
    maxz, // 4
    maxx,
    miny,
    maxz, // 5
    maxx,
    maxy,
    maxz, // 6
    minx,
    maxy,
    maxz, // 7
  ]);
  let indiceArray: number[] = [];

  if (options.back) indiceArray = [...indiceArray, 0, 2, 1, 0, 3, 2];
  if (options.front) indiceArray = [...indiceArray, 4, 5, 6, 4, 6, 7];

  if (options.top) indiceArray = [...indiceArray, 3, 7, 6, 3, 6, 2];
  if (options.bottom) indiceArray = [...indiceArray, 0, 1, 5, 0, 5, 4];

  if (options.right) indiceArray = [...indiceArray, 1, 2, 6, 1, 6, 5];
  if (options.left) indiceArray = [...indiceArray, 0, 4, 7, 0, 7, 3];
  const indices = new Uint16Array(indiceArray);

  geometry.setIndex(new BufferAttribute(indices, 1));
  geometry.setAttribute("position", new BufferAttribute(vertices, 3));
  geometry.computeVertexNormals();

  return geometry;
}

export function createQuarterCircle(radius: number, resolution: number = 10) {
  const points = [
    (new Vector2(radius, 0), new Vector2(0, 0), new Vector2(0, radius)),
  ];

  const path = new Path();
  path.currentPoint = new Vector2(radius, 0);
  path.lineTo(0, 0);
  path.lineTo(0, radius);
  path.absarc(0, 0, radius, 0, Math.PI / 2);

  return new BufferGeometry().setFromPoints(path.getSpacedPoints());
}

export function createQuarterCircle2(
  radius: number,
  thickness: number,
  resolution: number = 3,
  offset: Vector2 = new Vector2(0, 0)
) {
  const points: Vector3[] = [];
  const offset3 = new Vector3(offset.x, 0, -offset.y);

  points.push(new Vector3(0, 0, 0));
  points.push(new Vector3(radius, 0, 0));

  for (let i = 0; i <= resolution; i++) {
    const angle = (i / resolution) * (Math.PI / 2); // 0'dan 90 dereceye
    const p = new Vector3(
      Math.cos(angle) * radius,
      0,
      -Math.sin(angle) * radius
    );
    points.push(p);
  }

  points.push(new Vector3(0, 0, 0));
  points.push(new Vector3(0, 0, thickness));
  points.push(new Vector3(radius, 0, thickness));
  points.push(new Vector3(radius, 0));

  points.forEach((p) => p.add(offset3));

  const geometry = new BufferGeometry().setFromPoints(points);
  return geometry;
}

export function createWindow2DGeometry(
  radius: number,
  thickness: number,
  resolution: number = 3,
  offset: Vector2 = new Vector2(0, 0)
) {
  const points: Vector3[] = [];
  const offset3 = new Vector3(offset.x, 0, -offset.y);

  points.push(new Vector3(0, 0, 0));
  points.push(new Vector3(0, 0, thickness));

  points.push(new Vector3(0, 0, thickness));
  points.push(new Vector3(radius, 0, thickness));

  points.push(new Vector3(radius, 0, thickness));
  points.push(new Vector3(radius, 0, 0));

  points.push(new Vector3(radius, 0, 0));
  points.push(new Vector3(0, 0, 0));

  points.push(new Vector3(0, 0, 0.5 * thickness));
  points.push(new Vector3(radius, 0, 0.5 * thickness));

  points.forEach((p) => p.add(offset3));
  return new BufferGeometry().setFromPoints(points);
}

// ─── Compound OBB (XZ only, Y = full AABB height) ───────────────────
// 1) Sabit çözünürlükte XZ grid: üçgenler XZ’e rasterize + her vertex hücre damgası.
// 2) Boş hücreler yok sayılır; yalnız dolu alan işlenir.
// 3) En büyük bağlı dolu bileşen: kopuk ada / gürültü hücreleri elenir.
// 4) greedyRectangleCover: dolu alanı eksen-hizalı dikdörtgenlere böler (içi dolu kutu).
// 5) consolidate: iki parça yalnızca birleşimleri yine tek dolu dikdörtgen ise birleşir.
//    L koltuk → iki kol birleşince içi boş büyük kutu olacağından birleşmez; ayrı OBB kalır.
// 6) Tam kenarı MIN_COMPOUND_OBB_EDGE_M altındaki OBB’ler atılır; kalan yoksa tek AABB fallback.
//    Izgara kaba tutulur (DEFAULT_DECOMPOSE_GRID_RES) ki L iki-üç kutu kalsın.

/** XZ occupancy grid çözünürlüğü (NxN). İnce ızgara L kolunu fazla küçük kutuya böler. */
export const DEFAULT_DECOMPOSE_GRID_RES = 12;

/** Compound OBB kenarı bu değerin altındaysa atılır (m). İnce raster artığı elenir. */
export const MIN_COMPOUND_OBB_EDGE_M = 0.12;

/** Grid indeks alanında XZ dikdörtgeni (hücre koordinatları dahil aralık). */
export type GridRectXZ = {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
};

export interface MeshVertexData {
  positions: ArrayLike<number>;
  matrix: ArrayLike<number>;
  count: number;
  /** Varsa üçgenler `index`; yoksa pozisyonlar üçlü üçgen (0,1,2), (3,4,5), … */
  index?: ArrayLike<number> | null;
}

const _TRI_SAT_EPS = 1e-9;

/** 2D üçgen ile eksen hizalı dikdörtgen (XZ projeksiyonu); SAT, tam köşe/kenar durumları. */
function triRectOverlapXZ(
  ax: number,
  az: number,
  bx: number,
  bz: number,
  cx: number,
  cz: number,
  rx0: number,
  rz0: number,
  rx1: number,
  rz1: number,
): boolean {
  const axes: Array<[number, number]> = [
    [1, 0],
    [0, 1],
    [bz - az, -(bx - ax)],
    [cz - bz, -(cx - bx)],
    [az - cz, -(ax - cx)],
  ];
  for (let k = 0; k < axes.length; k++) {
    const nx = axes[k][0];
    const nz = axes[k][1];
    if (Math.hypot(nx, nz) < 1e-14) continue;
    const ta = ax * nx + az * nz;
    const tb = bx * nx + bz * nz;
    const tc = cx * nx + cz * nz;
    const t0 = Math.min(ta, tb, tc);
    const t1 = Math.max(ta, tb, tc);
    const r00 = rx0 * nx + rz0 * nz;
    const r10 = rx1 * nx + rz0 * nz;
    const r01 = rx0 * nx + rz1 * nz;
    const r11 = rx1 * nx + rz1 * nz;
    const r0 = Math.min(r00, r10, r01, r11);
    const r1 = Math.max(r00, r10, r01, r11);
    if (Math.max(t0, r0) > Math.min(t1, r1) + _TRI_SAT_EPS) return false;
  }
  return true;
}

function transformXZ(
  pos: ArrayLike<number>,
  m: ArrayLike<number>,
  vi: number,
  out: { x: number; z: number },
): void {
  const i3 = vi * 3;
  const ox = pos[i3];
  const oy = pos[i3 + 1];
  const oz = pos[i3 + 2];
  out.x = m[0] * ox + m[4] * oy + m[8] * oz + m[12];
  out.z = m[2] * ox + m[6] * oy + m[10] * oz + m[14];
}

/**
 * Pure function (worker-portable, no Three.js dependency).
 *
 * **Yüzey:** Her üçgen XZ’e izdüşürülür; hücre dikdörtgeni üçgenle kesişiyorsa dolu.
 * (Sadece vertex damgası büyük düz yüzeyde içi boş grid bırakırdı.)
 *
 * **Vertex:** Ayrıca her vertex kendi hücresini işaretler (çizgi / ince geometri).
 */
export function computeOccupancyGrid(
  meshes: MeshVertexData[],
  aabbMin: readonly [number, number, number],
  aabbMax: readonly [number, number, number],
  gridRes: number,
): boolean[][] {
  const grid = Array.from({ length: gridRes }, () =>
    new Array<boolean>(gridRes).fill(false),
  );
  const xRange = aabbMax[0] - aabbMin[0];
  const zRange = aabbMax[2] - aabbMin[2];
  if (xRange <= 0 || zRange <= 0) return grid;

  const cellW = xRange / gridRes;
  const cellD = zRange / gridRes;
  const invX = gridRes / xRange;
  const invZ = gridRes / zRange;
  const maxIdx = gridRes - 1;

  const pa = { x: 0, z: 0 };
  const pb = { x: 0, z: 0 };
  const pc = { x: 0, z: 0 };

  for (const mesh of meshes) {
    const { positions: pos, matrix: m, count, index } = mesh;

    const rasterOneTri = (ia: number, ib: number, ic: number) => {
      transformXZ(pos, m, ia, pa);
      transformXZ(pos, m, ib, pb);
      transformXZ(pos, m, ic, pc);

      const minXw = Math.min(pa.x, pb.x, pc.x);
      const maxXw = Math.max(pa.x, pb.x, pc.x);
      const minZw = Math.min(pa.z, pb.z, pc.z);
      const maxZw = Math.max(pa.z, pb.z, pc.z);

      let i0 = Math.floor((minXw - aabbMin[0]) / cellW);
      let i1 = Math.floor((maxXw - aabbMin[0]) / cellW);
      let j0 = Math.floor((minZw - aabbMin[2]) / cellD);
      let j1 = Math.floor((maxZw - aabbMin[2]) / cellD);
      i0 = Math.max(0, Math.min(maxIdx, i0));
      i1 = Math.max(0, Math.min(maxIdx, i1));
      j0 = Math.max(0, Math.min(maxIdx, j0));
      j1 = Math.max(0, Math.min(maxIdx, j1));

      for (let ci = i0; ci <= i1; ci++) {
        const rx0 = aabbMin[0] + ci * cellW;
        const rx1 = aabbMin[0] + (ci + 1) * cellW;
        for (let cj = j0; cj <= j1; cj++) {
          const rz0 = aabbMin[2] + cj * cellD;
          const rz1 = aabbMin[2] + (cj + 1) * cellD;
          if (
            triRectOverlapXZ(
              pa.x,
              pa.z,
              pb.x,
              pb.z,
              pc.x,
              pc.z,
              rx0,
              rz0,
              rx1,
              rz1,
            )
          ) {
            grid[ci][cj] = true;
          }
        }
      }
    };

    if (index && index.length >= 3) {
      for (let t = 0; t + 2 < index.length; t += 3) {
        rasterOneTri(index[t], index[t + 1], index[t + 2]);
      }
    } else if (count >= 3 && count % 3 === 0) {
      for (let t = 0; t + 2 < count; t += 3) {
        rasterOneTri(t, t + 1, t + 2);
      }
    }

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      const ox = pos[i3];
      const oy = pos[i3 + 1];
      const oz = pos[i3 + 2];
      const x = m[0] * ox + m[4] * oy + m[8] * oz + m[12];
      const z = m[2] * ox + m[6] * oy + m[10] * oz + m[14];
      const cx = Math.min(maxIdx, Math.max(0, Math.floor((x - aabbMin[0]) * invX)));
      const cz = Math.min(maxIdx, Math.max(0, Math.floor((z - aabbMin[2]) * invZ)));
      grid[cx][cz] = true;
    }
  }

  return grid;
}
/**
 * Tüm dolu hücreleri saran tek AABB (debug / analiz); çoklu OBB üretimi için kullanılmaz.
 */
export function boundingRectOfOccupiedCells(grid: boolean[][]): GridRectXZ | null {
  const rows = grid.length;
  if (rows === 0) return null;
  const cols = grid[0].length;
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      if (!grid[i][j]) continue;
      if (i < minX) minX = i;
      if (i > maxX) maxX = i;
      if (j < minZ) minZ = j;
      if (j > maxZ) maxZ = j;
    }
  }
  if (maxX < minX) return null;
  return { minX, maxX, minZ, maxZ };
}

/**
 * XZ gridde 4-komşu bağlı bileşenler; yalnızca **en büyük hücre sayısına** sahip bileşen(ler) kalır.
 * Ana gövdeden kopuk tek-hücre / gürültü adaları compound OBB üretmez (dışarıda kalan parça).
 * Eşit büyüklükte birden fazla ada varsa hepsi tutulur.
 */
export function filterOccupancyGridToLargestComponents(
  grid: boolean[][],
): boolean[][] {
  const rows = grid.length;
  if (rows === 0) return grid;
  const cols = grid[0].length;
  const labels = Array.from({ length: rows }, () =>
    new Array<number>(cols).fill(-1),
  );
  const sizes: number[] = [];

  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      if (!grid[i][j] || labels[i][j] !== -1) continue;
      const stack: [number, number][] = [[i, j]];
      let cnt = 0;
      const id = sizes.length;
      while (stack.length) {
        const [ci, cj] = stack.pop()!;
        if (ci < 0 || ci >= rows || cj < 0 || cj >= cols) continue;
        if (!grid[ci][cj] || labels[ci][cj] !== -1) continue;
        labels[ci][cj] = id;
        cnt++;
        stack.push([ci + 1, cj], [ci - 1, cj], [ci, cj + 1], [ci, cj - 1]);
      }
      sizes.push(cnt);
    }
  }

  if (sizes.length === 0) {
    return grid.map((r) => r.slice());
  }

  const maxSz = Math.max(...sizes);
  const keepIds = new Set<number>();
  for (let k = 0; k < sizes.length; k++) {
    if (sizes[k] === maxSz) keepIds.add(k);
  }

  return grid.map((row, i) =>
    row.map((_, j) => {
      const lb = labels[i][j];
      return lb >= 0 && keepIds.has(lb);
    }),
  );
}

function obbMeetsMinEdgeLengths(obb: OBB, minEdge: number): boolean {
  const hx = obb.halfSize.x * 2;
  const hy = obb.halfSize.y * 2;
  const hz = obb.halfSize.z * 2;
  return hx >= minEdge && hy >= minEdge && hz >= minEdge;
}

/**
 * Pure function (worker-portable).
 * Dolu hücreleri eksen-hizalı dikdörtgenlere böler (her çıktı parçası gerçek dolu kutu).
 */
export function greedyRectangleCover(grid: boolean[][]): GridRectXZ[] {
  const rows = grid.length;
  if (rows === 0) return [];
  const cols = grid[0].length;
  const processed = Array.from({ length: rows }, () =>
    new Array<boolean>(cols).fill(false),
  );
  const rects: GridRectXZ[] = [];

  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      if (!grid[i][j] || processed[i][j]) continue;

      let maxJ = j;
      while (maxJ + 1 < cols && grid[i][maxJ + 1] && !processed[i][maxJ + 1])
        maxJ++;

      let maxI = i;
      expand: while (maxI + 1 < rows) {
        for (let jj = j; jj <= maxJ; jj++) {
          if (!grid[maxI + 1][jj] || processed[maxI + 1][jj]) break expand;
        }
        maxI++;
      }

      for (let ii = i; ii <= maxI; ii++)
        for (let jj = j; jj <= maxJ; jj++) processed[ii][jj] = true;

      rects.push({ minX: i, maxX: maxI, minZ: j, maxZ: maxJ });
    }
  }

  return rects;
}

/**
 * İki grid dikdörtgeninin birleşimi tek bir eksen-hizalı dolu dikdörtgen ise birleşik kutuyu döndürür.
 * Köşegen iki hücre (satranç) veya L şekli gibi durumlarda null (absürt “dış bükey” kutu üretilmez).
 */
export function mergeTwoRectanglesIfUnionIsSolidBox(
  a: GridRectXZ,
  b: GridRectXZ,
): GridRectXZ | null {
  const minX = Math.min(a.minX, b.minX);
  const maxX = Math.max(a.maxX, b.maxX);
  const minZ = Math.min(a.minZ, b.minZ);
  const maxZ = Math.max(a.maxZ, b.maxZ);
  for (let i = minX; i <= maxX; i++) {
    for (let j = minZ; j <= maxZ; j++) {
      const inA = i >= a.minX && i <= a.maxX && j >= a.minZ && j <= a.maxZ;
      const inB = i >= b.minX && i <= b.maxX && j >= b.minZ && j <= b.maxZ;
      if (!inA && !inB) return null;
    }
  }
  return { minX, maxX, minZ, maxZ };
}

/** Greedy parçaları mümkün olduğunca birleştirir; her adımda sonuç yine tek dikdörtgen olmalıdır. */
export function consolidateAxisAlignedRectangles(rects: GridRectXZ[]): GridRectXZ[] {
  let list = rects.slice();
  let changed = true;
  while (changed) {
    changed = false;
    outer: for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const merged = mergeTwoRectanglesIfUnionIsSolidBox(list[i], list[j]);
        if (merged) {
          list.splice(j, 1);
          list.splice(i, 1);
          list.push(merged);
          changed = true;
          break outer;
        }
      }
    }
  }
  return list;
}

/**
 * Model-local compound OBB’ler (XZ).
 *
 * Grid: üçgen yüzey (index veya üçlü vertex) + vertex damgası; birleştirme yalnızca gerçek dikdörtgen birleşimde.
 * En büyük bağlı dolu bileşen tutulur; herhangi bir tam kenarı {@link MIN_COMPOUND_OBB_EDGE_M} altındaki kutular atılır.
 */
export function decomposeIntoOBBs(
  model: Object3D,
  aabb: Box3,
  gridRes = DEFAULT_DECOMPOSE_GRID_RES,
): OBB[] {
  const meshDataList: MeshVertexData[] = [];
  let totalVertexCount = 0;

  const rootInv = new Matrix4().copy(model.matrixWorld).invert();
  const meshToLocal = new Matrix4();

  model.traverse((child) => {
    if (!(child as Mesh).isMesh) return;
    const mesh = child as Mesh;
    const pos = mesh.geometry.attributes.position;
    if (!pos) return;

    meshToLocal.multiplyMatrices(rootInv, mesh.matrixWorld);
    const idx = mesh.geometry.index;
    meshDataList.push({
      positions: pos.array,
      matrix: Array.from(meshToLocal.elements),
      count: pos.count,
      index: idx ? idx.array : null,
    });
    totalVertexCount += pos.count;
  });

  const fallbackObb = () => {
    const obb = new OBB();
    const size = new Vector3();
    aabb.getCenter(obb.center);
    aabb.getSize(size);
    obb.halfSize.copy(size.multiplyScalar(0.5));
    return [obb];
  };

  if (totalVertexCount === 0) return fallbackObb();

  const aabbMin: [number, number, number] = [aabb.min.x, aabb.min.y, aabb.min.z];
  const aabbMax: [number, number, number] = [aabb.max.x, aabb.max.y, aabb.max.z];

  const gridRaw = computeOccupancyGrid(meshDataList, aabbMin, aabbMax, gridRes);
  const grid = filterOccupancyGridToLargestComponents(gridRaw);
  const initialRects = greedyRectangleCover(grid);
  const rects = consolidateAxisAlignedRectangles(initialRects);

  if (rects.length === 0) return fallbackObb();

  const xRange = aabb.max.x - aabb.min.x;
  const zRange = aabb.max.z - aabb.min.z;
  const yHalf = (aabb.max.y - aabb.min.y) * 0.5;
  const yCenter = (aabb.min.y + aabb.max.y) * 0.5;
  const cellW = xRange / gridRes;
  const cellD = zRange / gridRes;

  const obbs: OBB[] = [];
  for (const rect of rects) {
    const xMin = aabb.min.x + rect.minX * cellW;
    const xMax = aabb.min.x + (rect.maxX + 1) * cellW;
    const zMin = aabb.min.z + rect.minZ * cellD;
    const zMax = aabb.min.z + (rect.maxZ + 1) * cellD;

    const obb = new OBB();
    obb.center.set((xMin + xMax) * 0.5, yCenter, (zMin + zMax) * 0.5);
    obb.halfSize.set((xMax - xMin) * 0.5, yHalf, (zMax - zMin) * 0.5);
    if (obbMeetsMinEdgeLengths(obb, MIN_COMPOUND_OBB_EDGE_M)) {
      obbs.push(obb);
    }
  }

  return obbs.length > 0 ? obbs : fallbackObb();
}

const _obbDebugM4 = new Matrix4();

export type ObbWireframeOptions = {
  /** Varsayılan true. Debug overlay `false` geçirir; AI çakışma telleri derinlikte kalır. */
  depthTest?: boolean;
};

/** Tel kutu: OBB merkez/yarı boyut/dönüşümü ile model-local uzayda. */
export function createObbWireframeHelper(
  obb: OBB,
  color: number,
  options?: ObbWireframeOptions,
): LineSegments {
  const depthTest = options?.depthTest ?? true;
  const boxGeo = new BoxGeometry(
    obb.halfSize.x * 2,
    obb.halfSize.y * 2,
    obb.halfSize.z * 2,
  );
  const edges = new EdgesGeometry(boxGeo);
  boxGeo.dispose();
  const mat = new LineBasicMaterial({
    color,
    depthTest,
    toneMapped: depthTest,
  });
  const lines = new LineSegments(edges, mat);
  lines.position.copy(obb.center);
  _obbDebugM4.setFromMatrix3(obb.rotation);
  lines.quaternion.setFromRotationMatrix(_obbDebugM4);
  lines.name = "obbDebugWireframe";
  if (!depthTest) {
    lines.renderOrder = 9996;
    lines.frustumCulled = false;
  }
  (lines as unknown as { raycast: () => void }).raycast = () => {};
  return lines;
}


export function getLocalBoundingBox(root: Object3D): Box3 {
  const totalBox = new Box3();
  root.updateMatrixWorld(true); // Matrix zinciri güncel olsun

  const rootMatrixWorldInverse = new Matrix4().copy(root.matrixWorld).invert();

  root.traverse((child) => {
    if (child.userData.contributeToAO === "false") return;
    if ((child as Mesh).isMesh) {
      const mesh = child as Mesh;
      const geometry = mesh.geometry;

      // Geometri için local bounding box hesapla
      if (!geometry.boundingBox) {
        geometry.computeBoundingBox();
      }

      const localBox = geometry.boundingBox!.clone();

      const meshToWorld = new Matrix4().copy(mesh.matrixWorld);

      const meshToRootLocal = new Matrix4().multiplyMatrices(
        rootMatrixWorldInverse,
        meshToWorld
      );

      // 3. uygula
      localBox.applyMatrix4(meshToRootLocal);

      totalBox.union(localBox);
    }
  });

  return totalBox;
}

export function disposeObject3DDeep(root?: Object3D | null): void {
  if (!root) return;
  root.traverse((o) => {
    const mesh = o as Mesh;
    if (mesh.isMesh) {
      mesh.geometry?.dispose?.();
      const mat = mesh.material;
      if (Array.isArray(mat)) {
        for (const m of mat) disposeMaterialAndTextures(m);
      } else if (mat) {
        disposeMaterialAndTextures(mat);
      }
    }
  });
}

function disposeMaterialAndTextures(mat: Material): void {
  const rec = mat as unknown as Record<string, unknown>;
  for (const key of Object.keys(rec)) {
    const v = rec[key];
    if (v && typeof v === "object" && (v as { isTexture?: boolean }).isTexture) {
      (v as Texture).dispose();
    }
  }
  mat.dispose();
}
